import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Alert,
  Typography,
  CircularProgress,
  Autocomplete,
  IconButton,
} from '@mui/material';
import { Upload as UploadIcon, Add as AddIcon } from '@mui/icons-material';
import { getVendors, createVendor, getEntityModels } from '../services/api';

const MediaUploadModal = ({
  open,
  onClose,
  onUploadSuccess,
  selectedOrganization,
  mediaType = 'icon', // 'icon' | 'map'
  uploadFunction,
  updateFunction, // Function to call for updates (edit mode)
  title,
  helpText,
  fileFieldName = 'icon_image', // 'icon_image' | 'image_file'
  editMode = false, // Whether we're editing an existing item
  editData = null, // Existing item data for edit mode
}) => {
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    file: null,
    vendor: null,
    entity_model_id: null,
  });
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [entityModels, setEntityModels] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [vendorsLoaded, setVendorsLoaded] = useState(false);
  const [showNewVendor, setShowNewVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');

  // Load vendors when modal opens for icons
  useEffect(() => {
    if (open && mediaType === 'icon' && selectedOrganization) {
      loadVendors();
      loadEntityModels();
    } else if (open && mediaType !== 'icon') {
      setVendorsLoaded(true); // Mark as loaded for non-icon types
    }
  }, [open, mediaType, selectedOrganization]);

  // Pre-fill form when in edit mode - only after vendors are loaded
  useEffect(() => {
    // For icon edits, wait until vendors are loaded
    if (open && editMode && editData) {
      if (mediaType === 'icon' && !vendorsLoaded) {
        return; // Wait for vendors to load
      }

      setUploadForm({
        name: editData.name || '',
        description: editData.description || '',
        file: null, // File is optional in edit mode
        vendor: editData.vendor || null,
        entity_model_id:
          editData.entity_model_id || editData.entity_model?.id || null,
      });
    } else if (open && !editMode) {
      // Reset form for create mode
      setUploadForm({
        name: '',
        description: '',
        file: null,
        vendor: null,
        entity_model_id: null,
      });
    }
  }, [open, editMode, editData, mediaType, vendorsLoaded]);

  const loadVendors = async () => {
    setLoadingVendors(true);
    setVendorsLoaded(false);
    try {
      const vendorData = await getVendors();
      // Handle paginated response
      const vendorList = vendorData.results || vendorData;
      // Filter vendors by selected organization
      const orgVendors = vendorList.filter(
        v => v.organization === selectedOrganization
      );
      setVendors(orgVendors);
      setVendorsLoaded(true);
    } catch (error) {
      console.error('Failed to load vendors:', error);
      setVendorsLoaded(true); // Set to true even on error to unblock form
    }
    setLoadingVendors(false);
  };

  const loadEntityModels = async () => {
    try {
      const data = await getEntityModels();
      const list = data.results || data || [];
      const orgModels = list.filter(
        model => Number(model.organization) === Number(selectedOrganization)
      );
      const currentId =
        editData?.entity_model_id || editData?.entity_model?.id || null;
      setEntityModels(
        orgModels.filter(model => {
          if (!model.pin_icon) return true;
          if (currentId && model.id === currentId) return true;
          if (
            editData?.related_object_id &&
            model.pin_icon.id === editData.related_object_id
          ) {
            return true;
          }
          return false;
        })
      );
    } catch (error) {
      console.error('Failed to load entity models:', error);
    }
  };

  const handleAddNewVendor = async () => {
    if (!newVendorName.trim()) return;

    try {
      const newVendor = await createVendor({
        name: newVendorName.trim(),
        organization: selectedOrganization,
      });
      setVendors([...vendors, newVendor]);
      setUploadForm(prev => ({ ...prev, vendor: newVendor.id }));
      setNewVendorName('');
      setShowNewVendor(false);
    } catch (error) {
      setUploadError(
        error.response?.data?.detail ||
          error.response?.data?.name?.[0] ||
          'Failed to create vendor'
      );
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setUploadForm({
        name: '',
        description: '',
        file: null,
        vendor: null,
        entity_model_id: null,
      });
      setUploadError('');
      setShowNewVendor(false);
      setNewVendorName('');
      onClose();
    }
  };

  const handleUploadSubmit = async () => {
    // Validation: name is always required, file required only for create mode
    if (!uploadForm.name.trim()) {
      setUploadError('Name is required');
      return;
    }

    if (!editMode && !uploadForm.file) {
      setUploadError(
        `${mediaType === 'icon' ? 'Icon' : 'Image'} file is required`
      );
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      const submitData = {
        name: uploadForm.name.trim(),
        description: uploadForm.description.trim(),
      };

      // Add organization (always include if available, backend will ignore for updates)
      if (selectedOrganization) {
        submitData.organization = selectedOrganization;
      }

      // Add file if provided (required for create, optional for edit)
      if (uploadForm.file) {
        submitData[fileFieldName] = uploadForm.file;
      }

      // Add vendor field only for icons
      if (mediaType === 'icon' && uploadForm.vendor) {
        submitData.vendor = uploadForm.vendor;
      }

      if (mediaType === 'icon' && uploadForm.entity_model_id) {
        submitData.entity_model_id = uploadForm.entity_model_id;
      }

      let result;
      if (editMode) {
        // Call update function with item ID and data
        result = await updateFunction(editData.id, submitData);
      } else {
        // Call create function
        result = await uploadFunction(submitData);
      }

      // Reset form and close modal
      setUploadForm({
        name: '',
        description: '',
        file: null,
        vendor: null,
        entity_model_id: null,
      });
      setUploadError('');
      setShowNewVendor(false);
      setNewVendorName('');
      onClose();

      // Notify parent component of successful operation
      if (onUploadSuccess) {
        onUploadSuccess(result);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        `Failed to ${editMode ? 'update' : 'upload'} ${mediaType}`;
      setUploadError(errorMessage);
    }
    setUploading(false);
  };

  const handleFileChange = event => {
    const file = event.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = file => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB');
      return;
    }

    setUploadForm(prev => ({ ...prev, file: file }));
    setUploadError('');
  };

  // Drag and drop handlers
  const handleDragOver = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragLeave = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = event => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {title ||
          (editMode
            ? `Edit ${mediaType === 'icon' ? 'Custom Pin Icon' : 'Map Image'}`
            : `Upload ${mediaType === 'icon' ? 'Custom Pin Icon' : 'Map Image'}`)}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {uploadError && <Alert severity="error">{uploadError}</Alert>}

          <TextField
            label={mediaType === 'icon' ? 'Icon Name' : 'Name'}
            value={uploadForm.name}
            onChange={e =>
              setUploadForm(prev => ({ ...prev, name: e.target.value }))
            }
            required
            fullWidth
            disabled={uploading}
          />

          <TextField
            label="Description (Optional)"
            value={uploadForm.description}
            onChange={e =>
              setUploadForm(prev => ({
                ...prev,
                description: e.target.value,
              }))
            }
            multiline
            rows={2}
            fullWidth
            disabled={uploading}
          />

          {mediaType === 'icon' && (
            <Box>
              {!showNewVendor ? (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={vendors}
                    getOptionLabel={option => option.name || ''}
                    value={
                      vendors.find(v => v.id === uploadForm.vendor) || null
                    }
                    onChange={(event, newValue) => {
                      setUploadForm(prev => ({
                        ...prev,
                        vendor: newValue?.id || null,
                      }));
                    }}
                    loading={loadingVendors}
                    disabled={uploading}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Vendor (Optional)"
                        placeholder="Select a vendor"
                      />
                    )}
                  />
                  <IconButton
                    color="primary"
                    onClick={() => setShowNewVendor(true)}
                    disabled={uploading}
                    title="Add new vendor"
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    sx={{ flex: 1 }}
                    label="New Vendor Name"
                    value={newVendorName}
                    onChange={e => setNewVendorName(e.target.value)}
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddNewVendor();
                      }
                    }}
                    disabled={uploading}
                    autoFocus
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddNewVendor}
                    disabled={!newVendorName.trim() || uploading}
                  >
                    Add
                  </Button>
                  <Button
                    onClick={() => {
                      setShowNewVendor(false);
                      setNewVendorName('');
                    }}
                    disabled={uploading}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {mediaType === 'icon' && (
            <Autocomplete
              options={entityModels}
              getOptionLabel={option => option.name || ''}
              value={
                entityModels.find(m => m.id === uploadForm.entity_model_id) ||
                null
              }
              onChange={(event, newValue) => {
                setUploadForm(prev => ({
                  ...prev,
                  entity_model_id: newValue?.id || null,
                }));
              }}
              disabled={uploading}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Entity model (optional)"
                  helperText="Leave empty to keep this icon unassigned"
                />
              )}
            />
          )}

          <Box
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              textAlign: 'center',
              backgroundColor: 'background.paper',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              disabled={uploading}
              fullWidth
            >
              {editMode ? 'Change' : 'Choose'}{' '}
              {mediaType === 'icon' ? 'Icon' : 'Image'} File
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              {editMode
                ? 'Upload a new file to replace the current one'
                : 'or drag and drop an image file here'}
            </Typography>
            {uploadForm.file && (
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                Selected: {uploadForm.file.name}
              </Typography>
            )}
          </Box>

          <Alert severity="info">
            <Typography variant="body2">
              {helpText ||
                (mediaType === 'icon' ? (
                  <>
                    <strong>Recommended:</strong> PNG format with transparent
                    background.
                    <br />
                    <strong>Best sizes:</strong> 32x32, 64x64, or 128x128 pixels
                    for optimal display.
                    <br />
                    <strong>Max file size:</strong> 5MB. Use the size slider
                    after upload to adjust display scale.
                  </>
                ) : (
                  <>
                    <strong>Recommended:</strong> High-resolution image format
                    (PNG, JPG) for best map display.
                    <br />
                    <strong>Max file size:</strong> 5MB.
                  </>
                ))}
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleUploadSubmit}
          variant="contained"
          disabled={
            uploading ||
            !uploadForm.name.trim() ||
            (!editMode && !uploadForm.file)
          }
        >
          {uploading ? (
            <CircularProgress size={20} />
          ) : editMode ? (
            'Update'
          ) : (
            'Upload'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediaUploadModal;
